import React, { useEffect, useState } from "react";
import OrderLinesTable from "../../../../components/Orders/OrderLinesTable";
// Type on this page comes from the console scale (--lk-text-* in
// assets/css/lusuku-overrides.css): titles --lk-text-md, body --lk-text,
// button text --lk-text-sm, icon glyphs 16px. The inline 20/19/14px sizes it
// carried sat beside 13px panels and read as a different app (25 Oct 2026).
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Form,
  Modal,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { withStyles } from "@material-ui/core/styles";
import { FormControlLabel, Switch, Badge } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { io } from "socket.io-client";
import {
  MdCancel,
  MdLocalPrintshop,
  MdArrowBackIosNew,
  MdOutlineCreditCardOff,
  MdOutlineCreditCard,
  MdMap,
  MdRefresh,
} from "react-icons/md";
import { toast } from "react-toastify";

import "./RestaurantOrderDetails.css";
import { getUserFromLocalStorage } from "../../../../utilities/user.js";
import {
  getOrderDetailsToProcess,
  markOrderAsPaid,
  updateRestaurantOrderDeliveryStatus,
  assignRestaurantOrderToDeliveryAgent,
  re_assignRestaurantOrderToDeliveryAgent,
} from "../../../../actions/webOrderActions.js";
import RiderSelectionDropdown from "./RiderSelectionDropdown";
import { isRiderAssignmentLocked } from "../../../../utilities/orderRiderLock";
import ContactAction from "../../../../components/Orders/ContactAction";
import DropoffMap from "../../../../components/Maps/DropoffMap";
import { openReceiptForPrint } from "../../../../utilities/openReceipt";
import DeliveryScheduleBadge from "../../../../components/DeliveryScheduleBadge";
import {
  getDeliveryProgress,
  canVendorDispatch,
  canVendorMarkDelivered,
  AUDIENCE,
} from "../../../../utilities/orderDeliverySteps";
import OrderNextStep from "../../../../components/Orders/OrderNextStep";
// An ordered dish may have no photograph yet (menus are imported before
// the shoot), so these thumbnails need a fallback rather than a broken box.
// Fork, plate and knife on a plain near-white field: the restaurant
// placeholder for a dish that has not been photographed yet.
import menuPlaceholder from "assets/img/restaurant-dish-placeholder.png";
import socketAuthOptions from "../../../../utilities/socketAuth";
import useOrderLiveUpdates from "../../../../hooks/useOrderLiveUpdates";
import { getShippingAddressText } from "../../../../utilities/orderPresentation";
import AppSelect from "../../../../components/ui/AppSelect";
import VendorHandoffActions from "../../../../components/Orders/VendorHandoffActions";

const IOSSwitch = withStyles((theme) => ({
  root: {
    width: 42,
    height: 26,
    padding: 0,
    margin: theme.spacing(1),
  },
  switchBase: {
    padding: 1,
    "&$checked": {
      transform: "translateX(16px)",
      color: theme.palette.common.white,
      "& + $track": {
        backgroundColor: "#52d869",
        opacity: 1,
        border: "none",
      },
    },
    "&$focusVisible $thumb": {
      color: "#52d869",
      border: "6px solid #fff",
    },
  },
  thumb: {
    width: 24,
    height: 24,
  },
  track: {
    borderRadius: 26 / 2,
    border: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: theme.transitions.create(["background-color", "border"]),
  },
  checked: {},
  focusVisible: {},
}))(({ classes, ...props }) => {
  return (
    <Switch
      focusVisibleClassName={classes.focusVisible}
      disableRipple
      classes={{
        root: classes.root,
        switchBase: classes.switchBase,
        thumb: classes.thumb,
        track: classes.track,
        checked: classes.checked,
      }}
      {...props}
    />
  );
});

const isDevelopment = process.env.NODE_ENV === "development";

const SuperAdminRestaurantOrderDetails = () => {
  const user = getUserFromLocalStorage();
  const { orderId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [riders, setRiders] = useState([]);
  const [riderId, setRiderId] = useState("");
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [elapsedAssignedTime, setElapsedAssignedTime] = useState(0);
  const [elapsedDispatchedTime, setElapsedDispatchedTime] = useState(0);

  const [showMapModal, setShowMapModal] = useState(false);

  // const orderDetails = useSelector((state) => state.restaurantVendorOrders);
  const orderDetails = useSelector((state) => state.orderDetails);
  const { order, loading, error, isUpdateDeliveryStatusComplete } =
    orderDetails;

  // Live order news (a rider's pickup, a 3PL delivery, a decline) through the
  // one shared feed; see hooks/useOrderLiveUpdates.js. The page-level
  // listeners this replaces put `{ _id, delivery_status }` in the store as
  // the whole order, blanking the items.
  useOrderLiveUpdates(orderId, {
    onChange: (fresh) => {
      if (fresh?.delivery_status === "PROCESSING" && !fresh?.riderId) setRiderId("");
    },
  });

  // Log order as a json object
  console.log(order, "🚀🚀🚀🚀🚀🚀🚀🚀🚀🚀");

  useEffect(() => {
    dispatch(getOrderDetailsToProcess(orderId));
    if (isUpdateDeliveryStatusComplete) {
      dispatch(getOrderDetailsToProcess(orderId));
    }
    if (order?.riderId) {
      setRiderId(
        typeof order.riderId === "object" ? order.riderId._id : order.riderId
      );
    }
  }, [isUpdateDeliveryStatusComplete, orderId, dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SERVER_URL;

    const socket = io(
      socketUrl,
      socketAuthOptions({ userId: user?.result?._id })
    );

    socket.on("connect", () => {
      if (isDevelopment) {
        console.log("Webclient Connected");
      }
      // Emit the getRiders event after connecting
      socket.emit("getRiders");
    });

    socket.on("initialRidersList", (socketData) => {
      setRiders(socketData);
      setLoadingRiders(false);
    });

    socket.on("updatedRidersList", (updatedRiders) => {
      setRiders(updatedRiders);
    });

    socket.on("riderAdded", (newRider) => {
      setRiders((prevRiders) => [...prevRiders, newRider]);
    });

    socket.on("riderUpdated", (updatedRider) => {
      setRiders((prevRiders) =>
        prevRiders.map((rider) =>
          rider._id === updatedRider._id ? updatedRider : rider
        )
      );
    });

    socket.on("riderDeleted", (deletedRider) => {
      setRiders((prevRiders) =>
        prevRiders.filter((rider) => rider._id !== deletedRider._id)
      );
    });

    return () => {
      socket.disconnect();
    };
  }, [user?.result?._id]);

  const handleBack = () => {
    history.push("/admin/paginatedOrdersTable");
  };

  const handleUpdateStatus = (newStatus) => {
    if (newStatus === "ASSIGNED" && !riderId) {
      toast.warn("Please select a rider before assigning the order.");
      return;
    }
    if (newStatus === "ASSIGNED") {
      assignRestaurantOrderToRider();
    } else {
      dispatch(updateRestaurantOrderDeliveryStatus(order?._id, newStatus));
    }
  };

  const handleCancelOrder = () => {
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, "REJECTED"));
    setShowCancelModal(false);
  };

  // The receipt is its own document (the fiscal receipt with the EFRIS
  // markings when issued), not a print of this page.
  const handlePrint = () => openReceiptForPrint(order?._id);

  const handleMarkAsPaid = () => {
    if (user?.result?.userType !== "LUSUKU STAFF" && user?.result?.user_role !== "SUPERUSER") {
      toast.warn("🚫 You are not authorized to mark order as paid!");
      return;
    } else {
      dispatch(markOrderAsPaid(order?._id));
    }
  };

  const assignRestaurantOrderToRider = () => {
    const delivery_status = "ASSIGNED";
    const assignedTime = Date.now();
    toast.success(`Assigning order ${order?._id} to rider ${riderId}`);
    localStorage.setItem(`order_${order?._id}_assignedTime`, assignedTime);
    dispatch(
      assignRestaurantOrderToDeliveryAgent(order?._id, riderId, delivery_status)
    );
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  const handleDispatchRestaurantOrder = () => {
    const delivery_status = "DISPATCHED";
    const dispatchedTime = Date.now();
    localStorage.setItem(`order_${order?._id}_dispatchedTime`, dispatchedTime);
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  const handleDeliverRestaurantOrder = () => {
    const delivery_status = "DELIVERED";
    dispatch(updateRestaurantOrderDeliveryStatus(order?._id, delivery_status));
    setTimeout(() => {
      dispatch(getOrderDetailsToProcess(order?._id));
    }, 500);
  };

  // The delivery ladder lives in utilities/orderDeliverySteps.js. This was a
  // four-case switch whose `default: 0` emptied the bar for the six statuses
  // this page never writes itself: ACCEPTED and PICKED_UP come from the rider
  // app, PENDING_3PL / IN_TRANSIT / ARRIVED from the 3PL rail, FULFILLED from
  // the counter. A rider tapping Accept made the order look like it had gone
  // backwards to zero.
  //
  // This is an ADMIN surface, so it takes the progress and the button rules but
  // NOT the vendor-facing OrderNextStep banner, whose copy ("Your turn", "press
  // Dispatch Order") is addressed to the vendor working the order.
  //
  // NOTE: there is no "PAID" delivery_status. Payment state lives in
  // `isPaid`/`paymentStatus` (see utilities/paymentStatus.js); the
  // delivery_status enum in models/Orders.js has no such member.
  const getProgress = () => getDeliveryProgress(order);

  const getBadgeStyle = (status) => {
    switch (status) {
      case "PROCESSING":
        return {
          backgroundColor: "orange",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "ASSIGNED":
        return {
          backgroundColor: "blue",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DISPATCHED":
        return {
          backgroundColor: "purple",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "DELIVERED":
        return {
          backgroundColor: "green",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      case "REJECTED":
        return {
          backgroundColor: "red",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
      default:
        return {
          backgroundColor: "grey",
          color: "white",
          borderRadius: "10px",
          padding: "5px",
        };
    }
  };

  const getPaidBadgeStyle = (isPaid) => {
    return {
      backgroundColor: isPaid ? "green" : "#2C3A40",
      color: "white",
      borderRadius: "10px",
      padding: "3px",
    };
  };

  // Only an order still in PROCESSING can be cancelled here. This used to also
  // accept a "PAID" delivery_status, which is not a member of the delivery_status
  // enum and could never be stored — dead code that made the rule look broader
  // than it is. Behaviour is unchanged.
  const canCancelOrder = order?.delivery_status === "PROCESSING";

  const getDisabledStatusOptions = () => {
    const disabledOptions = {
      PROCESSING: order?.delivery_status !== "PROCESSING",
      ASSIGNED:
        order?.delivery_status !== "PROCESSING" &&
        !canVendorDispatch(order),
      DISPATCHED:
        !canVendorDispatch(order) &&
        !canVendorMarkDelivered(order),
      DELIVERED:
        !canVendorMarkDelivered(order) &&
        order?.delivery_status !== "DELIVERED",
      REJECTED:
        order?.delivery_status === "DELIVERED" ||
        order?.delivery_status === "REJECTED",
    };
    return disabledOptions;
  };

  const disabledStatusOptions = getDisabledStatusOptions();

  useEffect(() => {
    if (order?.delivery_status === "ASSIGNED") {
      const storedAssignedTime = localStorage.getItem(
        `order_${order?._id}_assignedTime`
      );
      if (storedAssignedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedAssignedTime, 10)) / 1000
        );
        setElapsedAssignedTime(elapsed);
      }
    }
    if (order?.delivery_status === "DISPATCHED") {
      const storedDispatchedTime = localStorage.getItem(
        `order_${order?._id}_dispatchedTime`
      );
      if (storedDispatchedTime) {
        const elapsed = Math.floor(
          (Date.now() - parseInt(storedDispatchedTime, 10)) / 1000
        );
        setElapsedDispatchedTime(elapsed);
      }
    }
    const interval = setInterval(() => {
      if (order?.delivery_status === "ASSIGNED") {
        setElapsedAssignedTime((prev) => prev + 1);
      }
      if (order?.delivery_status === "DISPATCHED") {
        setElapsedDispatchedTime((prev) => prev + 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [order?.delivery_status, order?._id]);

  if (loading) {
    return (
      <Spinner
        animation="grow"
        variant="primary"
        style={{
          marginTop: "70px",
          top: "50%",
          left: "50%",
          position: "fixed",
        }}
      />
    );
  }

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: "70px" }}>
        {error}
      </Alert>
    );
  }

  const handleShowMap = () => {
    setShowMapModal(true);
  };

  const handleCloseMap = () => {
    setShowMapModal(false);
  };

  // Reassign order to a new rider

  const handleReassignOrder = () => {
    const delivery_status = "PROCESSING";
    const currentRiderId =
      typeof order?.riderId === "object" ? order?.riderId?._id : order?.riderId;
    if (order?._id && currentRiderId) {
      dispatch(
        re_assignRestaurantOrderToDeliveryAgent(
          order?._id,
          currentRiderId,
          delivery_status,
          true
        )
      );
      setRiderId("");
    } else {
      toast.error("No rider currently assigned to this order.");
    }
  };
  return (
    <Container fluid>
      {order ? (
        <>
          <Row className="mb-2">
            <Col md="12">
              <Button variant="secondary" onClick={handleBack}>
                <MdArrowBackIosNew
                  style={{
                    marginRight: "3px",
                    fontSize: "16px",
                    color: "#ffff",
                    fontWeight: "bold",
                  }}
                />
                Back to Orders
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <Card className="shadow-sm">
                <Card.Header
                  className="justify-content-between align-items-center rod-card-header-inner"
                  style={{
                    backgroundColor: "#012030",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <div>
                    <Card.Title as="h4" style={{ color: "white", marginBottom: "4px" }}>
                      Order Details - Order #{order?._id}
                    </Card.Title>
                    <DeliveryScheduleBadge order={order} style={{ marginBottom: 4 }} />
                    {order?.createdAt && (
                      <small style={{ color: "#a8c6df", display: "block" }}>
                        {moment(order.createdAt).format("MMM D, YYYY · h:mm a")}
                      </small>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <VendorHandoffActions orderId={order?._id} />
                    <Button
                      style={{
                        backgroundColor: "#13678A",
                        color: "white",
                        border: "none",
                        fontSize: "var(--lk-text-sm)",
                        padding: "10px",
                      }}
                      onClick={handlePrint}
                      className="rod-print-btn"
                    >
                      Print Receipt
                      <MdLocalPrintshop style={{ marginLeft: "4px" }} />
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md="6" className="rod-left-col">
                      <h5
                        className="rod-section-title"
                        style={{
                          color: "#023059",
                          fontSize: "var(--lk-text-md)",
                          fontWeight: 600,
                        }}
                      >
                        Order Items
                      </h5>
                      <div className="rod-items-table-wrap">
                      <OrderLinesTable order={order} placeholderImage={menuPlaceholder} />
                      </div>
                    </Col>
                    <Col md="6" className="rod-right-col">
                      <h5
                        className="rod-section-title"
                        style={{
                          color: "#023059",
                          fontSize: "var(--lk-text-md)",
                          fontWeight: 600,
                        }}
                      >
                        Order Summary
                      </h5>
                      <Card
                        className="mb-3 p-3 shadow"
                        style={{ boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)" }}
                      >
                        {/* <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Customer Name:
                          </strong>
                          {} {order?.customerName}
                        </p> */}

                        {/* Customer Name */}
                        <div style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            {" "}
                            Customer Name:
                          </strong>
                          <p
                            style={{
                              color: "#fefe",
                              backgroundColor: "#012030",
                              padding: "2px",
                              borderRadius: "5px",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            { } {order?.customerName}
                          </p>
                        </div>

                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Payment Method:
                          </strong>
                          { } {order?.paymentMethod}
                        </p>
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Items Price:
                          </strong>
                          { } Ush {order?.itemsPrice?.toLocaleString()}
                        </p>
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Tax Price:
                          </strong>
                          { } Ush {order?.taxPrice?.toLocaleString()}
                        </p>
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Total Price:
                          </strong>
                          { } Ush {order?.totalPrice?.toLocaleString()}
                        </p>
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Payment Status:
                          </strong>{" "}
                          <Badge
                            overlap="rectangular"
                            style={getPaidBadgeStyle(order?.isPaid)}
                          >
                            {order?.isPaid ? (
                              <MdOutlineCreditCard
                                style={{
                                  fontSize: "var(--lk-text)",
                                  verticalAlign: "middle",
                                }}
                              />
                            ) : (
                              <MdOutlineCreditCardOff
                                style={{
                                  fontSize: "var(--lk-text)",
                                  verticalAlign: "middle",
                                }}
                              />
                            )}
                          </Badge>
                        </p>
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Order Status:
                          </strong>{" "}
                          <Badge
                            overlap="rectangular"
                            style={getBadgeStyle(order?.delivery_status)}
                          >
                            {order?.delivery_status}
                          </Badge>
                        </p>
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <strong
                            style={{
                              color: "#012030",
                              fontSize: "var(--lk-text)",
                            }}
                          >
                            Order Date:
                          </strong>
                          { }{" "}
                          {moment(order?.createdAt).format(
                            "MMMM Do YYYY, h:mm:ss a"
                          )}
                        </p>
                      </Card>
                      <h5
                        style={{
                          color: "#023059",
                          fontSize: "var(--lk-text-md)",
                          fontWeight: 600,
                        }}
                      >
                        Shipping Address
                      </h5>
                      <Card
                        className="p-3 shadow"
                        style={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)" }}
                      >
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          {getShippingAddressText(order?.shippingAddress, "No address recorded")}
                        </p>
                        <ContactAction
                          label="Customer"
                          name={order?.customerName}
                          phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                          compact
                          style={{ marginBottom: 10 }}
                        />
                        {/* The same drop-off block as the admin order page:
                            place name, precision chip, Show map, Send pin,
                            Copy pin. This page printed the raw coordinates
                            instead. The route map modal below stays. */}
                        {Number.isFinite(Number(order?.shippingAddress?.latLng?.lat)) &&
                          Number.isFinite(Number(order?.shippingAddress?.latLng?.lng)) && (
                            <DropoffMap
                              lat={order.shippingAddress.latLng.lat}
                              lng={order.shippingAddress.latLng.lng}
                              address={getShippingAddressText(order?.shippingAddress, "")}
                              shippingAddress={order?.shippingAddress}
                              orderRef={order?.order_number || order?._id}
                              rider={order?.riderId}
                              country={order?.country}
                            />
                          )}
                        <p style={{ fontSize: "var(--lk-text)" }}>
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="map-tooltip">
                                View Delivery Address on Map!
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="link"
                              onClick={handleShowMap}
                              style={{
                                padding: 0,
                                marginLeft: "10px",
                                size: "30px",
                              }}
                            >
                              <MdMap size={30} color="#063" />
                            </Button>
                          </OverlayTrigger>
                        </p>
                      </Card>
                      {/* Map Modal */}
                      <Modal
                        show={showMapModal}
                        onHide={handleCloseMap}
                        size="lg"
                      >
                        <Modal.Header closeButton>
                          <Modal.Title>
                            Delivery Location Map ||{" "}
                            {getShippingAddressText(order?.shippingAddress)}
                          </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                          <iframe
                            width="100%"
                            height="450"
                            frameBorder="0"
                            style={{ border: 0 }}
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${order?.shippingAddress?.latLng?.lat},${order?.shippingAddress?.latLng?.lng}`}
                            allowFullScreen
                          ></iframe>
                        </Modal.Body>
                        <Modal.Footer>
                          <Button variant="secondary" onClick={handleCloseMap}>
                            Close
                          </Button>
                        </Modal.Footer>
                      </Modal>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col
                      md="6"
                      className="d-flex flex-column justify-content-between"
                    >
                      {/* <Form.Group controlId="formRiderSelect" className="shadow p-3 h-100" style={{ boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.2)", height: "100%" }}>
                                                <Form.Label>Select Rider</Form.Label>
                                                <Form.Control
                                                    as="select"
                                                    value={order?.riderId?._id || riderId}
                                                    onChange={(e) => setRiderId(e.target.value)}
                                                    disabled={!!order?.riderId || order?.delivery_status === "DELIVERED"}
                                                >
                                                    <option value="">Select Rider</option>
                                                    {loadingRiders ? (
                                                        <option value="" disabled>
                                                            Searching for riders...
                                                        </option>
                                                    ) : (
                                                        riders?.map((rider) => (
                                                            <option key={rider._id} value={rider._id}>
                                                                {rider.firstName + " " + rider.lastName} || {rider.onlineStatus}
                                                            </option>
                                                        ))
                                                    )}
                                                </Form.Control>
                                                {loadingRiders && (
                                                    <div style={{ marginLeft: "2px" }}>
                                                        <Spinner animation="border" size="sm" />
                                                    </div>
                                                )}
                                                {order?.riderId && (
                                                    <p style={{ marginTop: "10px", fontSize: "var(--lk-text-sm)", fontStyle: "italic" }}>
                                                        Assigned to:{" "}
                                                        {order?.riderId?.firstName + " " + order?.riderId?.lastName}
                                                        <MdSportsMotorsports
                                                            style={{
                                                                marginLeft: "5px",
                                                                fontSize: "16px",
                                                                color: "green",
                                                            }} />
                                                        <br />
                                                        {order?.delivery_status === "ASSIGNED" && (
                                                            <span>
                                                                Time Elapsed after Assigning: {Math.floor(elapsedAssignedTime / 3600)}h {Math.floor((elapsedAssignedTime % 3600) / 60)}m {elapsedAssignedTime % 60}s
                                                            </span>
                                                        )}
                                                        {order?.delivery_status === "DISPATCHED" && (
                                                            <span>
                                                                Time Elapsed from Dispatch: {Math.floor(elapsedDispatchedTime / 3600)}h {Math.floor((elapsedDispatchedTime % 3600) / 60)}m {elapsedDispatchedTime % 60}s
                                                            </span>
                                                        )}
                                                    </p>
                                                )}
                                                {!order?.riderId && (
                                                    <Button
                                                        variant="primary"
                                                        onClick={assignRestaurantOrderToRider}
                                                        className="mt-2"
                                                        disabled={!riderId || loadingRiders || order?.delivery_status === "DELIVERED"}
                                                        style={{
                                                            visibility: riderId ? "visible" : "hidden",
                                                            backgroundColor: !riderId || order?.delivery_status === "DELIVERED" ? "#8C1F28" : "#8C1F28",
                                                            color: !riderId ? "black" : "white",
                                                        }}
                                                    >
                                                        Assign Order to Rider
                                                    </Button>
                                                )}
                                            </Form.Group> */}
                      <RiderSelectionDropdown
                        order={order}
                        riderId={riderId}
                        setRiderId={setRiderId}
                        loadingRiders={loadingRiders}
                        riders={riders}
                        assignRestaurantOrderToRider={
                          assignRestaurantOrderToRider
                        }
                        elapsedAssignedTime={elapsedAssignedTime}
                        elapsedDispatchedTime={elapsedDispatchedTime}
                        onRiderSelect={(newRiderId) => setRiderId(newRiderId)}
                      />
                      {order?.delivery_status !== "PROCESSING" &&
                        !isRiderAssignmentLocked(order) && (
                          <Button
                            variant="secondary"
                            onClick={handleReassignOrder}
                            className="mt-2"
                            style={{ width: "100%" }}
                          >
                            <>
                              <MdRefresh style={{ marginRight: "5px" }} />
                              Re-assign Order to different Rider!
                            </>
                          </Button>
                        )}
                    </Col>
                    <Col md="6">
                      <Form.Group
                        controlId="formOrderStatus"
                        className="shadow p-3 h-100"
                        style={{ boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.2)" }}
                      >
                        <Form.Label>Update Order Status</Form.Label>
                        <AppSelect block
                          value={order?.delivery_status}
                          onChange={(e) => handleUpdateStatus(e.target.value)}
                        >
                          <option
                            value="PROCESSING"
                            disabled={disabledStatusOptions.PROCESSING}
                          >
                            1. Processing
                          </option>
                          <option
                            value="ASSIGNED"
                            disabled={disabledStatusOptions.ASSIGNED}
                          >
                            2. Assigned
                          </option>
                          <option
                            value="DISPATCHED"
                            disabled={disabledStatusOptions.DISPATCHED}
                          >
                            3. Dispatched
                          </option>
                          <option
                            value="DELIVERED"
                            disabled={disabledStatusOptions.DELIVERED}
                          >
                            4. Delivered
                          </option>
                          <option
                            value="REJECTED"
                            disabled={disabledStatusOptions.REJECTED}
                          >
                            5. Cancel Order
                          </option>
                        </AppSelect>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col className="d-flex justify-content-end align-items-center rod-action-row">
                      <div
                        style={{
                          cursor: !canCancelOrder ? "not-allowed" : "pointer",
                          backgroundColor: !canCancelOrder
                            ? "#8C1F28"
                            : "transparent",
                          borderRadius: "5px",
                          padding: "2px",
                          marginRight: "5px",
                          width: "160px",
                          height: "40px",
                        }}
                      >
                        <Button
                          variant="danger"
                          onClick={() => setShowCancelModal(true)}
                          className="mr-2"
                          disabled={!canCancelOrder}
                          style={{
                            backgroundColor: !canCancelOrder
                              ? "transparent"
                              : "red",
                            color: !canCancelOrder ? "white" : "white",
                            border: "none",
                            fontSize: "var(--lk-text-sm)",
                            width: "100%",
                            height: "100%",
                            padding: "6px",
                          }}
                        >
                          <MdCancel
                            style={{
                              fontSize: "16px",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: "5px",
                            }}
                          />
                          Cancel Order
                        </Button>
                      </div>
                      {/* components/Orders/ContactAction: a real tel: link on
                          a phone, copy on a desktop. The old button only
                          raised a toast. */}
                      <ContactAction
                        label="Customer"
                        name={order?.customerName}
                        phone={order?.shippingAddress?.customerNumber || order?.shippingAddress?.recipientPhone}
                        style={{ marginRight: 5 }}
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col md="12">
                      {/* Admin voice: this reports what the vendor and rider are doing.
                          See the AUDIENCE note in utilities/orderDeliverySteps.js. */}
                      <OrderNextStep
                        order={order}
                        audience={AUDIENCE.ADMIN}
                        style={{ marginBottom: "12px" }}
                      />
                      <ProgressBar
                        now={getProgress()}
                        label={`${getProgress()}%`}
                        style={{ height: "30px" }}
                        variant={
                          order?.delivery_status === "DELIVERED"
                            ? "success"
                            : undefined
                        }
                      />
                    </Col>
                  </Row>
                  <Row className="mt-4">
                    <Col md="12" className="d-flex justify-content-between rod-dispatch-row">
                      <div
                        style={{
                          cursor:
                            !canVendorDispatch(order)
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            !canVendorDispatch(order)
                              ? "#4B4952"
                              : "transparent",
                          borderRadius: "5px",
                          padding: "2px",
                        }}
                      >
                        <Button
                          variant="warning"
                          onClick={handleDispatchRestaurantOrder}
                          disabled={!canVendorDispatch(order)}
                          style={{
                            backgroundColor:
                              !canVendorDispatch(order)
                                ? "transparent"
                                : "orange",
                            color:
                              !canVendorDispatch(order)
                                ? "white"
                                : "black",
                            border: "none",
                            fontSize: "var(--lk-text-sm)",
                          }}
                        >
                          Dispatch Order
                        </Button>
                      </div>
                      <div
                        style={{
                          cursor:
                            !canVendorMarkDelivered(order)
                              ? "not-allowed"
                              : "pointer",
                          backgroundColor:
                            !canVendorMarkDelivered(order)
                              ? "green"
                              : "transparent",
                          borderRadius: "5px",
                          padding: "2px",
                        }}
                      >
                        <Button
                          variant="success"
                          onClick={handleDeliverRestaurantOrder}
                          disabled={!canVendorMarkDelivered(order)}
                          style={{
                            backgroundColor:
                              !canVendorMarkDelivered(order)
                                ? "transparent"
                                : "green",
                            color:
                              !canVendorMarkDelivered(order)
                                ? "white"
                                : "black",
                            border: "none",
                            fontSize: "var(--lk-text-sm)",
                          }}
                        >
                          Mark as Delivered
                        </Button>
                      </div>
                      <div
                        style={{
                          cursor: order?.isPaid ? "not-allowed" : "pointer",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <IOSSwitch
                              checked={order?.isPaid}
                              onChange={handleMarkAsPaid}
                              color="primary"
                              disabled={
                                order?.delivery_status !== "DELIVERED" ||
                                order?.isPaid
                              }
                            />
                          }
                          label={order?.isPaid ? "Paid" : "Mark as Paid"}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Modal
            show={showCancelModal}
            onHide={() => setShowCancelModal(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>Confirm Cancellation</Modal.Title>
            </Modal.Header>
            <Modal.Body>Are you sure you want to cancel this order?</Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowCancelModal(false)}
              >
                Close
              </Button>
              <Button variant="danger" onClick={handleCancelOrder}>
                Cancel Order
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      ) : (
        <Alert variant="warning" style={{ marginTop: "70px" }}>
          No order details found.
        </Alert>
      )}
    </Container>
  );
};

export default SuperAdminRestaurantOrderDetails;
